/*
 * /intake/$patientId (page)
 */
import {
	Badge,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Container,
	Page,
	Placeholder,
} from "@emr/react-ui"
import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { PatientIntakeForm } from "../../features/intakes/patient-intake-form"
import { PatientHeader } from "../../features/patient/patient-header"
import { trpc } from "../../trpc"

export const Route = createFileRoute("/intake/$patientId")({
	component: IntakePage,
})

const MOCK_PATIENT = {
	firstName: "...",
	lastName: "",
	dateOfBirth: null,
	bloodType: null,
	allergies: null,
	emergencyContactName: null,
	emergencyContactPhone: null,
}

function IntakePage() {
	const { patientId } = Route.useParams()
	const [isDirty, setIsDirty] = useState(false)

	const { data: patient, isLoading: patientLoading } =
		trpc.patient.getByFilter.useQuery({ where: { id: patientId } })

	const { data: existingIntake, isLoading: intakeLoading } =
		trpc.patientIntake.getByFilter.useQuery({
			where: { patientId, isLatest: true, active: true },
		})

	const isLoading = patientLoading || intakeLoading
	const displayPatient = patient ?? (MOCK_PATIENT as any)

	return (
		<Page centerVertical={false}>
			<PatientHeader patient={displayPatient} patientId={patientId} isDirty={isDirty} />
			<Container width="xl" padding="lg" gap="lg">

				{isLoading ? (
					<Placeholder title="Loading..." />
				) : (
					<>
						{/* Summary of existing intake */}
						{existingIntake && (
							<Card>
								<CardHeader>
									<div className="flex items-center justify-between">
										<div>
											<CardTitle>Current Intake</CardTitle>
											<CardDescription>
												Submitted{" "}
												{new Date(existingIntake.createdAt).toLocaleDateString()}
											</CardDescription>
										</div>
										<Badge variant="secondary">On File</Badge>
									</div>
								</CardHeader>
								<CardContent>
									<div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
										{existingIntake.familyDoctor && (
											<p>
												<span className="font-medium">Family Doctor: </span>
												{existingIntake.familyDoctor}
											</p>
										)}
										{existingIntake.occupation && (
											<p>
												<span className="font-medium">Occupation: </span>
												{existingIntake.occupation}
											</p>
										)}
										{existingIntake.maritalStatus && (
											<p>
												<span className="font-medium">Marital Status: </span>
												{existingIntake.maritalStatus === "married"
													? "Married / Common Law"
													: "Single"}
											</p>
										)}
										{existingIntake.allergies && (
											<p>
												<span className="font-medium">Allergies: </span>
												{existingIntake.allergies}
											</p>
										)}
										{existingIntake.familyDoctor && (
											<p>
												<span className="font-medium">Consent: </span>
												{existingIntake.consentSigned ? "Signed" : "Not signed"}
											</p>
										)}
									</div>
								</CardContent>
							</Card>
						)}

						{/* Form */}
						<Card>
							<CardHeader>
								<CardTitle>
									{existingIntake ? "Update Patient Intake" : "New Patient Intake"}
								</CardTitle>
								{existingIntake && (
									<CardDescription>
										Saving will create a new version and archive the current one.
									</CardDescription>
								)}
							</CardHeader>
							<CardContent>
								<PatientIntakeForm
									patientId={patientId}
									existingIntake={existingIntake ?? undefined}
									onDirtyChange={setIsDirty}
								/>
							</CardContent>
						</Card>
					</>
				)}
			</Container>
		</Page>
	)
}
